import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Dimensions, Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';

import { formatResponseTime } from '../constants';

import MetricDetailLayout from './MetricDetailLayout';
import { readGrandTotal } from './rollupHelpers';
import { StatTile } from './statTiles';
import useSlideoutSharedData, { useSlideoutDimensionCounts } from './useSlideoutSharedData';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

const HISTOGRAM_BUCKETS = [
  { label: '< 1hr', max: 1 * 3600 },
  { label: '1-2hr', max: 2 * 3600 },
  { label: '2-3hr', max: 3 * 3600 },
  { label: '3-4hr', max: 4 * 3600 },
  { label: '4-5hr', max: 5 * 3600 },
  { label: '5-6hr', max: 6 * 3600 },
  { label: '6-7hr', max: 7 * 3600 },
  { label: '7-8hr', max: 8 * 3600 },
  { label: '8-9hr', max: 9 * 3600 },
  { label: '9-10hr', max: 10 * 3600 },
  { label: '10+ hr', max: Infinity },
];

export default function ResponseTimeDetailHeader({ loading, rollups }: Props) {
  const { fastestResponder, slowestResponder, countsLoading } = useSlideoutSharedData();

  const {
    names: histogramSeconds,
    counts: histogramCounts,
    loading: histogramLoading,
  } = useSlideoutDimensionCounts(Dimensions.RESPONSE_TIME_SECONDS, 'referral-slideout-response-time-histogram');

  const avgResponseTime = readGrandTotal(rollups, Metrics.RESPONSE_TIME_SECONDS_AVERAGE);

  const { histogramSeries, respondedCount } = useMemo(() => {
    const buckets = HISTOGRAM_BUCKETS.map(() => 0);
    let total = 0;
    histogramSeconds.forEach((raw, i) => {
      const value = Number(raw);
      if (!Number.isFinite(value)) return;
      const count = histogramCounts[i] ?? 0;
      const idx = HISTOGRAM_BUCKETS.findIndex((b) => value < b.max);
      const target = idx === -1 ? HISTOGRAM_BUCKETS.length - 1 : idx;
      buckets[target] += count;
      total += count;
    });
    return { histogramSeries: buckets, respondedCount: total };
  }, [histogramSeconds, histogramCounts]);

  const chartOptions = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: 'column', style: { fontFamily: 'Lato' }, height: 280 },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        enabled: true,
        align: 'right',
        verticalAlign: 'top',
        symbolRadius: 2,
        itemStyle: { fontSize: '12px', color: colors.black75, fontWeight: 'normal' },
      },
      xAxis: {
        categories: HISTOGRAM_BUCKETS.map((b) => b.label),
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '10px', color: colors.black50 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        labels: { style: { fontSize: '10px', color: colors.black50 } },
      },
      tooltip: { backgroundColor: colors.chartTooltipBackground, style: { color: colors.white }, padding: 12 },
      plotOptions: { column: { borderRadius: 2, pointPadding: 0.05, groupPadding: 0.08 } },
      series: [{ type: 'column', name: 'Response Time', data: histogramSeries, color: colors.primaryBlue }],
    }),
    [histogramSeries]
  );

  const tiles = (
    <>
      <StatTile
        label='Avg. Response Time'
        loading={loading}
        primary={formatResponseTime(avgResponseTime)}
        addendum={respondedCount > 0 ? `Based on ${respondedCount.toLocaleString()} referrals` : undefined}
      />
      <StatTile
        label='Fastest Responder'
        loading={countsLoading}
        primary={fastestResponder?.name ?? '—'}
        addendum={fastestResponder ? formatResponseTime(fastestResponder.value) : undefined}
      />
      <StatTile
        label='Slowest Responder'
        loading={countsLoading}
        primary={slowestResponder?.name ?? '—'}
        addendum={slowestResponder ? formatResponseTime(slowestResponder.value) : undefined}
      />
    </>
  );

  return (
    <MetricDetailLayout
      tiles={tiles}
      chartTitle='Response Time Distribution'
      chartLoading={histogramLoading}
      placeholderHeight={240}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
